/**
 * PATH: packages/ui/src/components/settings/email-label-prefs/PersonalCategoryRow.tsx
 * FILE_ID: UI-SETTINGS-LABELPREFS-ROW-001
 * PURPOSE: One editable custom-category row (swatch + label + AI auto-file
 *          hint, with inline rename / recolour / describe / delete). Used by
 *          both the Personal tier and the custom slice of the Organisation
 *          tier.
 * WHY: Operator ask 2026-06-16 — personal labelling tier, user-addable.
 *      2026-07-03 wave 2: the description is fed to the classifier so the AI
 *      auto-files mail into the label ("route Facebook notifications into my
 *      Noise label") — describing a label is how auto-filing is switched on.
 */

import { useState } from 'react'
import { Check, FolderTree, Pencil, Sparkles, Trash2, X, Loader2 } from 'lucide-react'
import { cn } from '../../../lib/utils'
import type { LabelCategory } from './types'

/** Curated swatches — calm, legible hues that read in light & dark mode. */
export const PERSONAL_COLOR_PALETTE = [
  '#16A34A', '#0EA5E9', '#6366F1', '#8B5CF6', '#EC4899',
  '#EF4444', '#F97316', '#EAB308', '#14B8A6', '#64748B',
]

interface PersonalCategoryRowProps {
  category: LabelCategory
  busy?: boolean
  onSave: (patch: {
    label?: string
    color?: string
    description?: string
    parent_id?: number | null
  }) => void
  onDelete: () => void
  /** Valid "Nest under" targets: top-level labels of the SAME tier,
   *  excluding this row. Omitted/empty → the nesting select is hidden
   *  (e.g. a label that itself has children can't be nested). */
  parentOptions?: Array<{ id: number; label: string }>
  /** True when this row is nested under a parent — the section indents it. */
  nested?: boolean
}

export function PersonalCategoryRow({
  category,
  busy,
  onSave,
  onDelete,
  parentOptions,
  nested,
}: PersonalCategoryRowProps) {
  const [editing, setEditing] = useState(false)
  const [label, setLabel] = useState(category.label)
  const [color, setColor] = useState(category.color || '#6366F1')
  const [description, setDescription] = useState(category.description ?? '')
  const [parentId, setParentId] = useState<number | null>(
    category.parent_id ?? null,
  )

  const startEdit = () => {
    setLabel(category.label)
    setColor(category.color || '#6366F1')
    setDescription(category.description ?? '')
    setParentId(category.parent_id ?? null)
    setEditing(true)
  }

  const commit = () => {
    const trimmed = label.trim()
    if (!trimmed) return
    const patch: {
      label: string
      color: string
      description: string
      parent_id?: number | null
    } = { label: trimmed, color, description: description.trim() }
    if ((category.parent_id ?? null) !== parentId) patch.parent_id = parentId
    onSave(patch)
    setEditing(false)
  }

  if (editing) {
    return (
      <div className="flex flex-col gap-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-2.5">
        <div className="flex items-center gap-2">
          <span
            className="w-4 h-4 rounded-full shrink-0 ring-1 ring-black/10"
            style={{ backgroundColor: color }}
          />
          <input
            autoFocus
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit()
              if (e.key === 'Escape') setEditing(false)
            }}
            maxLength={80}
            className="flex-1 px-2 py-1 rounded-md text-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
          />
          <button
            onClick={commit}
            disabled={busy}
            className="p-1.5 rounded-md text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/30 disabled:opacity-50"
            aria-label="Save category"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          </button>
          <button
            onClick={() => setEditing(false)}
            className="p-1.5 rounded-md text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700"
            aria-label="Cancel"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 pl-6">
          {PERSONAL_COLOR_PALETTE.map((hex) => (
            <button
              key={hex}
              onClick={() => setColor(hex)}
              className={cn(
                'w-5 h-5 rounded-full ring-1 ring-black/10 transition-transform',
                color.toLowerCase() === hex.toLowerCase() && 'ring-2 ring-offset-1 ring-slate-500 scale-110',
              )}
              style={{ backgroundColor: hex }}
              aria-label={`Use colour ${hex}`}
            />
          ))}
        </div>
        {parentOptions && parentOptions.length > 0 && (
          <div className="pl-6">
            <label className="flex items-center gap-1.5 text-[11px] font-medium text-slate-500 dark:text-slate-400 mb-1">
              <FolderTree className="w-3 h-3 text-slate-400" />
              Nest under
            </label>
            <select
              value={parentId ?? ''}
              onChange={(e) =>
                setParentId(e.target.value ? Number(e.target.value) : null)
              }
              aria-label={`Nest ${category.label} under`}
              className="w-full px-2 py-1 rounded-md text-xs border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
            >
              <option value="">No parent (top level)</option>
              {parentOptions.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="pl-6">
          <label className="flex items-center gap-1.5 text-[11px] font-medium text-slate-500 dark:text-slate-400 mb-1">
            <Sparkles className="w-3 h-3 text-violet-500" />
            AI auto-file hint
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder='Describe what belongs here and the AI will file matching mail automatically, e.g. "Social media notifications - Facebook, LinkedIn digests". Leave blank to file manually only.'
            maxLength={400}
            rows={2}
            aria-label={`AI auto-file hint for ${category.label}`}
            className="w-full px-2 py-1.5 rounded-md text-xs border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white resize-none"
          />
        </div>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'group flex items-center gap-2.5 rounded-lg px-2.5 py-2 hover:bg-slate-50 dark:hover:bg-slate-800/40',
        nested && 'ml-6 border-l-2 border-slate-200 dark:border-slate-700 pl-3',
      )}
    >
      <span
        className="w-3.5 h-3.5 rounded-full shrink-0 ring-1 ring-black/10"
        style={{ backgroundColor: category.color || '#6366F1' }}
      />
      <span className="flex-1 text-sm text-slate-800 dark:text-slate-200 truncate">
        {category.label}
      </span>
      {(category.description ?? '').trim() !== '' && (
        <span
          className="inline-flex items-center gap-1 text-[10px] font-medium text-violet-600 dark:text-violet-400"
          title={`AI auto-files here: ${category.description}`}
        >
          <Sparkles className="w-3 h-3" />
          Auto-files
        </span>
      )}
      <button
        onClick={startEdit}
        className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 opacity-0 group-hover:opacity-100 transition-opacity"
        aria-label={`Edit ${category.label}`}
      >
        <Pencil className="w-3.5 h-3.5" />
      </button>
      <button
        onClick={onDelete}
        disabled={busy}
        className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
        aria-label={`Delete ${category.label}`}
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  )
}

export default PersonalCategoryRow
